import React from "react"
import { ThemeProvider } from "styled-components"

import { getViewport } from "utils"
import useLazyImport from "hooks/useLazyImport"

import SectionHeading from "components/section-heading"
import CustomSlider from "./customSlider"
import AnimatedSlider from "./animated-slider"

const GridGallery = ({
  gridData,
  title,
  subtitle,
  elRef,
  dataId,
  hideOnPageLoad,
  isFocusLayout,
  leftFocus,
  isFocusedItem,
  isRailItem,
  parentLayout,
  useAnimatedSlider = false,
  ...props
}) => {
  const device = getViewport()

  const defaultComponentTheme = (oldTheme) => {
    if (oldTheme.component) return oldTheme
    return {
      ...oldTheme,
      component: {
        background: "initial",
        text: oldTheme.colors.text.primary,
        sectionHeading: oldTheme.contentfulTemplates.Standard.sectionHeading,
      },
    }
  }

  const styles = useLazyImport(() => import("./styles"))
  if (!styles) {
    return null
  }

  const { desktopColumns, largeDesktopColumns, mobileColumns, tabletColumns, items } = gridData

  const itemType = items[0].internal.type

  const { GridGallerySection, Carousel } = styles

  // If using animated slider, render it directly with gridData
  if (useAnimatedSlider) {
    return (
      <ThemeProvider theme={defaultComponentTheme}>
        <AnimatedSlider
          gridData={gridData}
          title={title}
          subtitle={subtitle}
          hideOnPageLoad={hideOnPageLoad}
          dataId={dataId}
          elRef={elRef}
          {...props}
        />
      </ThemeProvider>
    )
  }

  // Rest of the original GridGallery logic for regular slider
  const GRID_COLUMN_SIZES_PER_TYPE = {
    ContentfulPerson: {
      desktopL: 5,
      desktop: 4,
      tablet: 3,
      mobile: 2,
    },
    ContentfulCard: {
      desktopL: 3,
      desktop: 3,
      tablet: 2,
      mobile: 1,
    },
    ContentfulProductHighlight: {
      desktopL: 3,
      desktop: 3,
      tablet: 2,
      mobile: 1,
    },
    ContentfulAccordionItem: {
      desktopL: 1,
      desktop: 1,
      tablet: 1,
      mobile: 1,
    },
    ContentfulFeatureHighlight: {
      desktopL: 3,
      desktop: 3,
      tablet: 3,
      mobile: 2,
    },
    ContentfulClassModule: {
      desktopL: 2,
      desktop: 2,
      tablet: 2,
      mobile: 1,
    },
    ContentfulTestimonial: {
      desktopL: 2,
      desktop: 2,
      tablet: 2,
      mobile: 1,
    },
    ContentfulTextWithMedia: {
      desktopL: 3,
      desktop: 3,
      tablet: 2,
      mobile: 1,
    },
    ContentfulTextBlurb: {
      desktopL: 2,
      desktop: 2,
      tablet: 2,
      mobile: 1,
    },
  }

  const numColumns = {
    desktopL:
      largeDesktopColumns === 4
        ? 2
        : largeDesktopColumns || GRID_COLUMN_SIZES_PER_TYPE[itemType].desktopL,
    desktop:
      largeDesktopColumns === 4
        ? 2
        : desktopColumns || GRID_COLUMN_SIZES_PER_TYPE[itemType].desktop,
    tablet:
      largeDesktopColumns === 4 ? 2 : tabletColumns || GRID_COLUMN_SIZES_PER_TYPE[itemType].tablet,
    mobile: mobileColumns || GRID_COLUMN_SIZES_PER_TYPE[itemType].mobile,
  }

  const COL_MARGINS_PER_TYPE = {
    ContentfulPerson: "2.6rem",
    ContentfulCard: "1.5rem",
    ContentfulProductHighlight: "1.5rem",
    ContentfulAccordionItem: "0rem",
    ContentfulFeatureHighlight: "2.4rem",
    ContentfulClassModule: "3rem",
    ContentfulTestimonial: device === "mobile" ? "2rem" : "0.75rem",
    ContentfulTextWithMedia: "2rem",
    ContentfulTextBlurb: "0rem",
  }
  let isTestimonialWithoutImages = false
  let colMargin = COL_MARGINS_PER_TYPE[itemType]
  if (itemType === "ContentfulTestimonial") {
    if ((items[0].attribution && items[0].attribution.image) || items[0].attributionImage) {
      colMargin = device === "mobile" ? "2rem" : "3rem"
    } else {
      isTestimonialWithoutImages = true
    }
  }

  const itemsForColumns = numColumns[device]

  return (
    <ThemeProvider theme={defaultComponentTheme}>
      <GridGallerySection
        hideOnPageLoad={hideOnPageLoad}
        data-exp-id={dataId}
        ref={elRef}
        itemType={itemType}
        {...props}
      >
        <SectionHeading textComponent title={title} subtitle={subtitle} />
        <Carousel
          step={itemsForColumns}
          visibleSlides={itemsForColumns}
          totalSlides={items.length}
          naturalSlideWidth={1}
          naturalSlideHeight={1}
          infinite
          isIntrinsicHeight
        >
          <CustomSlider
            device={device}
            items={items}
            colMargin={colMargin}
            isTestimonialWithoutImages={isTestimonialWithoutImages}
            isFocusLayout={isFocusLayout}
            leftFocus={leftFocus}
            isFocusedItem={isFocusedItem}
            isRailItem={isRailItem}
            parentLayout={parentLayout}
          />
        </Carousel>
      </GridGallerySection>
    </ThemeProvider>
  )
}

export default GridGallery
